import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/Api";
import { usePermisos } from "../../context/PermisosContext";

const tiposEquipo = [
  { carpeta: "Sensores", nombre: "Equipo de lectura" },
  { carpeta: "Efectores", nombre: "Equipo de control" },
  { carpeta: "Mixto", nombre: "Equipo de lectura y control" },
];
const fechaLocal = (fecha) => fecha && !Number.isNaN(new Date(fecha).getTime())
  ? new Date(fecha).toLocaleString("es-GT", { dateStyle: "medium", timeStyle: "short" }) : "Sin comprobar";

const AcercaDe = ({ version = process.env.REACT_APP_VERSION || "No disponible" }) => {
  const { can } = usePermisos();
  const [datos, setDatos] = useState(null);
  const [equipos, setEquipos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [ultimaConsulta, setUltimaConsulta] = useState(null);
  const [copia, setCopia] = useState("");
  const vigente = useRef(false);
  const enCurso = useRef(false);

  const comprobar = async () => {
    if (enCurso.current) return;
    enCurso.current = true;
    setCargando(true);
    setCopia("");
    try {
      const [sistema, ...versiones] = await Promise.allSettled([
        api.get("/Sistema/diagnostico"),
        ...tiposEquipo.map(async ({ carpeta }) => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 12000);
          try {
            const respuesta = await fetch(`/Firmware/${carpeta}/manifest.json`, { cache: "no-store", signal: controller.signal });
            if (!respuesta.ok) throw new Error("No disponible");
            return await respuesta.json();
          } finally { clearTimeout(timeout); }
        }),
      ]);
      if (!vigente.current) return;
      setDatos(sistema.status === "fulfilled" ? sistema.value.data : null);
      setError(sistema.status === "fulfilled" ? "" :
        [401, 403].includes(sistema.reason?.response?.status)
          ? "No pudimos verificar tu sesión. Vuelve a iniciar sesión y comprueba de nuevo."
          : "No pudimos conectar con el sistema. Revisa tu conexión e inténtalo de nuevo.");
      setEquipos(versiones.map((resultado, i) => ({
        ...tiposEquipo[i],
        version: resultado.status === "fulfilled" && typeof resultado.value.version === "string"
          ? resultado.value.version : "No disponible",
      })));
      setUltimaConsulta(new Date().toISOString());
    } finally {
      enCurso.current = false;
      if (vigente.current) setCargando(false);
    }
  };

  useEffect(() => {
    vigente.current = true;
    comprobar();
    return () => { vigente.current = false; };
    // La comprobación se realiza al abrir esta sección o al pulsar el botón.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const conexion = cargando ? "Comprobando…" : datos?.api === "Disponible" ? "Conectado" : "Sin confirmar";
  const registros = cargando ? "Comprobando…" : datos?.baseDatos === "Disponible" ? "Disponibles" : datos ? "No disponibles en este momento" : "Sin confirmar";
  const sesion = cargando ? "Comprobando…" : datos?.sesion === "Vigente" ? "Activa" : "Sin confirmar";
  const resumen = [
    "Control de estanques",
    `Versión de esta página: ${version}`,
    `Versión del servicio: ${datos?.version || "No disponible"}`,
    `Conexión: ${conexion}`,
    `Acceso a los registros: ${registros}`,
    `Sesión: ${sesion}`,
    `Última comprobación: ${fechaLocal(ultimaConsulta)}`,
    ...equipos.map((equipo) => `${equipo.nombre} — versión disponible: ${equipo.version}`),
  ].join("\n");
  const copiar = async () => {
    try { await navigator.clipboard.writeText(resumen); setCopia("Información copiada. Puedes pegarla en tu mensaje de ayuda."); }
    catch { setCopia("No se pudo copiar automáticamente. Selecciona el resumen de abajo y cópialo manualmente."); }
  };
  const tarjeta = "rounded-2xl border border-white/10 bg-slate-950/50 p-5 sm:p-6";
  const boton = "inline-flex items-center rounded-lg border border-cyan-500/30 bg-cyan-950/30 px-4 py-2.5 text-sm font-semibold text-cyan-100 hover:bg-cyan-900/40 disabled:opacity-50";

  return (
    <div className="rounded-2xl border border-emerald-500/20 bg-[#071014]/90 p-5 text-white sm:p-8">
      <header className="mb-8 flex items-center gap-4">
        <img src="/Logo_peces.png" alt="" className="h-16 w-16 object-contain" />
        <div>
          <h1 className="text-2xl font-bold text-emerald-300 sm:text-3xl">Acerca de</h1>
          <p className="mt-1 text-lg">Control de estanques</p>
          <p className="mt-2 text-sm text-slate-400">Información de tu sistema y datos que puedes compartir cuando necesites ayuda.</p>
        </div>
      </header>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className={tarjeta} aria-labelledby="acerca-versiones">
          <h2 id="acerca-versiones" className="text-lg font-semibold text-emerald-300">Versiones del sistema</h2>
          <dl className="mt-5 space-y-4">
            <div><dt className="text-sm text-slate-400">Esta página</dt><dd className="mt-1 font-semibold">{version}</dd></div>
            <div><dt className="text-sm text-slate-400">Servicio del sistema</dt><dd className="mt-1 font-semibold">{cargando ? "Consultando…" : datos?.version || "No disponible"}</dd></div>
          </dl>
          <p className="mt-5 text-sm leading-6 text-slate-400">Estas versiones ayudan a identificar la actualización que estás usando al solicitar asistencia.</p>
        </section>

        <section className={tarjeta} aria-labelledby="acerca-estado">
          <h2 id="acerca-estado" className="text-lg font-semibold text-emerald-300">Estado de tu conexión</h2>
          <dl className="mt-5 space-y-3" aria-live="polite">
            <div className="flex flex-wrap justify-between gap-2"><dt className="text-slate-400">Conexión con el sistema</dt><dd>{conexion}</dd></div>
            <div className="flex flex-wrap justify-between gap-2"><dt className="text-slate-400">Acceso a los registros</dt><dd>{registros}</dd></div>
            <div className="flex flex-wrap justify-between gap-2"><dt className="text-slate-400">Tu sesión</dt><dd>{sesion}</dd></div>
          </dl>
          {error && <p role="alert" className="mt-4 text-sm text-amber-200">{error}</p>}
          <p className="mt-4 text-xs text-slate-400">Última comprobación: {fechaLocal(ultimaConsulta)} (hora de tu navegador).</p>
          <button type="button" className={`${boton} mt-4`} disabled={cargando} onClick={comprobar}>{cargando ? "Comprobando…" : "Comprobar de nuevo"}</button>
          <p className="mt-4 text-sm leading-6 text-slate-400">Esta comprobación revisa tu acceso al sistema. La conexión de cada equipo se consulta en Dispositivos.</p>
        </section>

        <section className={tarjeta} aria-labelledby="acerca-equipos">
          <h2 id="acerca-equipos" className="text-lg font-semibold text-emerald-300">Versiones disponibles para los equipos</h2>
          <p className="mt-2 text-sm leading-6 text-slate-400">Son las versiones publicadas para instalar. Un equipo puede tener instalada una versión diferente.</p>
          <dl className="mt-5 space-y-3">
            {(equipos.length ? equipos : tiposEquipo).map((equipo) => <div key={equipo.carpeta} className="flex flex-wrap justify-between gap-2">
              <dt className="text-slate-300">{equipo.nombre}</dt><dd className="font-semibold">{cargando ? "Consultando…" : equipo.version || "No disponible"}</dd>
            </div>)}
          </dl>
          {can("DISPOSITIVOS_VIEW") && can("DISPOSITIVOS_TAB_EDITAR_VIEW")
            ? <Link className={`${boton} mt-5`} to="/dashboard/admin?tab=Dispositivos&sub=administrar">Consultar mis dispositivos</Link>
            : <p className="mt-5 text-sm text-slate-400">Pide al administrador de tu cuenta que revise la versión instalada y la última conexión de tus equipos.</p>}
        </section>

        <section className={tarjeta} aria-labelledby="acerca-ayuda">
          <h2 id="acerca-ayuda" className="text-lg font-semibold text-emerald-300">Cuando necesites ayuda</h2>
          <p className="mt-3 text-sm leading-6 text-slate-300">En Contáctanos puedes elegir a quién escribir. Describe qué intentabas hacer, qué ocurrió y cuándo. Si el problema es de un equipo, incluye su nombre y el mensaje que aparece en pantalla.</p>
          <p className="mt-3 text-sm leading-6 text-slate-400">El resumen contiene versiones y el estado de la conexión. Puedes revisarlo antes de compartirlo.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" onClick={copiar} disabled={cargando} className={boton}>Copiar información para ayuda</button>
            <Link className={boton} to="/dashboard/admin?tab=Cont%C3%A1ctanos">Ir a Contáctanos</Link>
          </div>
          <p role="status" className="mt-3 text-sm text-cyan-200">{copia}</p>
          <details className="mt-3 text-sm text-slate-400"><summary className="cursor-pointer">Ver información para compartir</summary>
            <textarea aria-label="Información para ayuda" readOnly value={resumen} rows={10} className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-950 p-3 text-slate-300" />
          </details>
        </section>
      </div>
    </div>
  );
};

export default AcercaDe;
